import { useState } from 'react';
import { allCountries } from 'country-telephone-data';
import DarwinTypography from 'components/darwin-ui/Typography';
import {
  ActionsDropdown,
  DarwinCol,
  DarwinFlex,
  DarwinRow,
} from 'components/darwin-ui';
import styles from './AddressDetails.module.css';
import {
  AddressBookSectionFormData,
  CompanyAddress,
} from 'types/organizationTypes';
import { useTranslation } from 'react-i18next';
import Icon from 'components/darwin-ui/Icon';
import { useCreateOrganizationFormContext } from 'contexts/CreateOrganizationFormContext';
import { GenericDeleteModal } from 'components/ui/GenericDeleteModal';

const { Title, Text } = DarwinTypography;

interface AddressDetailsProps {
  address: CompanyAddress;
  handleUpdateDisplayForm: (updateDisplayForm: boolean) => void;
  handleResetFormValues: (data: AddressBookSectionFormData) => void;
  handleDeleteAddress: () => void;
}

export const AddressDetails = ({
  address,
  handleUpdateDisplayForm,
  handleResetFormValues,
  handleDeleteAddress,
}: AddressDetailsProps) => {
  const { t } = useTranslation();
  const { setIsEditingAddress } = useCreateOrganizationFormContext();
  const [displayDeleteModal, setDisplayDeleteModal] = useState<boolean>(false);

  const menuItems = () => [
    {
      key: 'edit',
      label: (
        <DarwinFlex
          gap={8}
          align="center"
          onClick={() => {
            setIsEditingAddress(true);
            const {
              id,
              active,
              isPrimary,
              description,
              createdAt,
              updatedAt,
              locations,
              contacts,
              ...rest
            } = address;
            const editPayload: AddressBookSectionFormData = {
              ...rest,
              country:
                allCountries.find(count => count.name === rest.country)?.name ||
                '',
              // this will change later when we work on contacts
              contactName: contacts[contacts.length - 1].contactName,
              contactPhone: {
                phoneCode: contacts[contacts.length - 1].phoneCode.slice(1),
                phoneNumber: contacts[contacts.length - 1].phoneNumber,
              },
              contactEmail: contacts[contacts.length - 1].contactEmail || '',
            };
            handleUpdateDisplayForm(true);
            handleResetFormValues(editPayload);
          }}
        >
          <Icon name={'edit'} size="sm" color="inherit" />
          Edit
        </DarwinFlex>
      ),
    },
    {
      key: 'delete',
      label: (
        <DarwinFlex
          gap={8}
          align="center"
          onClick={() => {
            setDisplayDeleteModal(true);
          }}
        >
          <Icon name={'delete'} size="sm" color="inherit" />
          Delete
        </DarwinFlex>
      ),
    },
  ];

  return (
    <>
      <div className={styles.container}>
        <div className={styles.header}>
          <Title level={5} className={styles.addressName}>
            {address.addressName}
          </Title>
          <ActionsDropdown getMenuItems={menuItems} />
        </div>
        <DarwinRow gutter={16}>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.primary-address')}
            </Text>
            <div className={`${styles.value}`}>{address.addressLine1}</div>
          </DarwinCol>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.secondary-address')}
            </Text>
            <div className={styles.value}>{address.addressLine2}</div>
          </DarwinCol>
          <DarwinCol
            className={`${styles.dividerBorderTop} ${styles.dividerBorderRight}`}
            span={12}
          >
            <Text className={styles.label}>
              {t('create-organization-form.city')}
            </Text>
            <div className={styles.value}>{address.city}</div>
          </DarwinCol>
          <DarwinCol className={styles.dividerBorderTop} span={12}>
            <Text className={styles.label}>
              {t('create-organization-form.state')}
            </Text>
            <div className={styles.value}>{address.state}</div>
          </DarwinCol>
          <DarwinCol
            className={`${styles.dividerBorderTop} ${styles.dividerBorderRight}`}
            span={12}
          >
            <Text className={styles.label}>
              {t('create-organization-form.country')}
            </Text>
            <div className={styles.value}>{address.country}</div>
          </DarwinCol>
          <DarwinCol className={`${styles.dividerBorderTop}`} span={12}>
            <Text className={styles.label}>
              {t('create-organization-form.zip-code')}
            </Text>
            <div className={styles.value}>{address.zipCode}</div>
          </DarwinCol>
          <DarwinCol
            className={`${styles.dividerBorderTop} ${styles.dividerBorderRight}`}
            span={12}
          >
            <Text className={styles.label}>
              {t('create-organization-form.contact-name')}
            </Text>
            <div className={styles.value}>
              {address.contacts?.[address.contacts.length - 1].contactName}
            </div>
          </DarwinCol>
          <DarwinCol
            className={`${styles.dividerBorderTop}`}
            span={12}
          ></DarwinCol>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.email')}
            </Text>
            <div className={styles.value}>
              {address.contacts?.[address.contacts.length - 1].contactEmail}
            </div>
          </DarwinCol>
          <DarwinCol className={styles.dividerBorderTop} span={24}>
            <Text className={styles.label}>
              {t('create-organization-form.phone')}
            </Text>
            <div className={styles.value}>
              {`${address.contacts?.[address.contacts.length - 1].phoneCode} ${
                address.contacts?.[address.contacts.length - 1].phoneNumber
              }`}
            </div>
          </DarwinCol>
        </DarwinRow>
      </div>
      <GenericDeleteModal
        open={displayDeleteModal}
        onCancel={() => {
          setDisplayDeleteModal(false);
        }}
        onOk={() => {
          handleDeleteAddress();
          setDisplayDeleteModal(false);
        }}
        bodyText={t('create-organization-form.delete-modal-body', {
          fieldName: 'dirección',
        })}
      />
    </>
  );
};
